import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Flag, Camera } from "lucide-react";
import { EQUIP_STATUS, StatusBadge } from "./equipmentMeta";

const REQUESTABLE = ["under_repair", "quarantined", "lost"];
const inputCls = "w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand";

const RequestPhoto = ({ requestId, photo }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    api.get(`/equipment/status-requests/${requestId}/photos/${photo.id}`, { responseType: "blob" })
      .then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [requestId, photo.id]);
  if (!url) return <div className="w-16 h-16 bg-amber-100 animate-pulse" />;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={`request-photo-${photo.id}`}>
      <img src={url} alt={photo.filename} className="w-16 h-16 object-cover border border-amber-300 hover:ring-2 hover:ring-brand" />
    </a>
  );
};

export const PendingRequestsPanel = ({ requests, onDecided }) => {
  const [busy, setBusy] = useState(null);
  if (requests.length === 0) return null;

  const decide = async (r, decision) => {
    setBusy(r.id);
    try {
      await api.post(`/equipment/status-requests/${r.id}/${decision}`);
      toast.success(decision === "approve" ? `${r.equipment_name} marked ${EQUIP_STATUS[r.requested_status].label}` : "Request rejected");
      onDecided();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-amber-50 border border-amber-300 mb-5" data-testid="status-requests-panel">
      <div className="px-4 py-2.5 border-b border-amber-200 flex items-center gap-2">
        <Flag size={16} className="text-amber-700" />
        <h3 className="font-heading font-bold tracking-tight text-amber-900">{requests.length} status change request{requests.length > 1 ? "s" : ""} waiting</h3>
      </div>
      <ul className="divide-y divide-amber-100">
        {requests.map((r) => (
          <li key={r.id} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`status-request-${r.id}`}>
            <div className="flex-1 min-w-[220px]">
              <div className="text-sm font-bold text-zinc-900 flex items-center gap-2 flex-wrap">
                {r.asset_id && <span className="font-mono text-brand text-xs">{r.asset_id}</span>}
                {r.equipment_name} <span className="text-zinc-400 font-normal">→</span> <StatusBadge status={r.requested_status} />
              </div>
              <div className="text-xs text-zinc-600 mt-0.5 break-words">"{r.reason}" — {r.requested_by_name}</div>
              {r.photos?.length > 0 && (
                <div className="flex gap-2 mt-2">{r.photos.map((p) => <RequestPhoto key={p.id} requestId={r.id} photo={p} />)}</div>
              )}
            </div>
            <button disabled={busy === r.id} onClick={() => decide(r, "approve")} data-testid={`approve-request-${r.id}`}
              className="bg-emerald-600 text-white px-3 py-1.5 text-xs font-bold flex items-center gap-1 hover:bg-emerald-700 disabled:opacity-50">
              <Check size={14} /> Approve
            </button>
            <button disabled={busy === r.id} onClick={() => decide(r, "reject")} data-testid={`reject-request-${r.id}`}
              className="bg-white border border-zinc-300 text-zinc-700 px-3 py-1.5 text-xs font-bold flex items-center gap-1 hover:border-red-500 hover:text-red-600 disabled:opacity-50">
              <X size={14} /> Reject
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const RequestStatusForm = ({ equipment, pending, onRequested }) => {
  const options = REQUESTABLE.filter((s) => s !== equipment.status);
  const [picked, setStatus] = useState(options[0]);
  const status = options.includes(picked) ? picked : options[0];
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);

  const pickFiles = (e) => {
    const list = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    e.target.value = "";
    if (list.some((f) => f.size > 5 * 1024 * 1024)) toast.error("Each photo must be 5 MB or smaller");
    setFiles([...files, ...list.filter((f) => f.size <= 5 * 1024 * 1024)].slice(0, 2));
  };

  if (pending) {
    return (
      <div className="bg-amber-50 border border-amber-300 px-3 py-2 text-sm text-amber-900" data-testid="pending-request-note">
        Waiting for approval: mark as <strong>{EQUIP_STATUS[pending.requested_status].label}</strong> — "{pending.reason}"
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/equipment/${equipment.id}/status-requests`, { status, reason: reason.trim() });
      let failed = 0;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/equipment/status-requests/${r.data.id}/photos`, fd).catch(() => { failed += 1; });
      }
      if (failed) toast.warning(`Request sent, but ${failed} photo(s) failed to upload`);
      else toast.success("Request sent to services managers");
      setReason("");
      setFiles([]);
      onRequested();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };


  return (
    <form onSubmit={submit} className="space-y-2" data-testid="request-status-form">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand border-b border-zinc-200 pb-1">Flag a problem</div>
      <div className="flex gap-2 flex-wrap">
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="request-status-select" className={`${inputCls} w-auto`}>
          {options.map((s) => <option key={s} value={s}>{EQUIP_STATUS[s].label}</option>)}
        </select>
        <input required minLength={3} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Why? e.g. hook latch broken" data-testid="request-reason-input" className={`${inputCls} flex-1 min-w-0 basis-48`} />
        <button type="submit" disabled={busy || reason.trim().length < 3} data-testid="request-status-submit"
          className="bg-amber-500 text-white px-3 py-2 text-sm font-bold flex items-center gap-1 hover:bg-amber-600 disabled:opacity-50">
          <Flag size={14} /> Request
        </button>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {files.length < 2 && (
          <label className="text-xs font-bold text-zinc-600 hover:text-brand flex items-center gap-1 cursor-pointer" data-testid="request-photo-label">
            <Camera size={14} /> Add photo (optional, up to 2)
            <input type="file" accept="image/*" multiple className="hidden" onChange={pickFiles} data-testid="request-photo-input" />
          </label>
        )}
        {files.map((f, i) => (
          <span key={i} className="text-xs bg-zinc-100 border border-zinc-300 px-2 py-1 flex items-center gap-1" data-testid={`request-photo-chip-${i}`}>
            {f.name.slice(0, 24)}
            <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} data-testid={`request-photo-remove-${i}`} className="text-zinc-400 hover:text-red-600"><X size={12} /></button>
          </span>
        ))}
      </div>
    </form>
  );
};
